import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useQuery, useMutation } from '@tanstack/react-query';
import { queryClient } from '@/lib/queryClient';
import { getLinkedAccountsOptions, deleteUnlinkProviderOptions } from '@/services/queryOptions/authQueryOptions';
import { useToast } from '@/hooks/use-toast';
import useModalStore from '@/stores/useModalStore';
import { initiateOAuthFlow, initiateGoogleOAuthFlow } from '@/lib/oauth';
import LinkedAccountCard from './LinkedAccountCard';
import { Loader2, AlertCircle } from 'lucide-react';
import { trackEvent } from '@/utils/analytics';
import { EventAction } from '@/types/analytics';
import { authKeys } from '@/constant/queryKeys';

const AccountManagementModal: React.FC = () => {
  const { accountManagement, setAccountManagementModal } = useModalStore();
  const { show: showModal } = accountManagement;
  const { toast } = useToast();
  
  const [isLinking, setIsLinking] = useState<'x' | 'google' | null>(null);
  const [unlinkingProvider, setUnlinkingProvider] = useState<'x' | 'google' | null>(null);

  // Fetch linked accounts status
  const { 
    data: linkedAccounts, 
    isLoading: loadingAccounts,
    error: fetchError,
  } = useQuery({
    ...getLinkedAccountsOptions(),
    enabled: showModal,
    retry: 2,
  });

  // Unlink mutation
  const unlinkMutation = useMutation({
    ...deleteUnlinkProviderOptions(),
    onSuccess: (data, provider) => {
      trackEvent('account_action', EventAction.ACCOUNT_UNLINKED, {
        provider,
        success: true,
      });

      toast({
        title: 'Account unlinked',
        description: `Your ${provider === 'x' ? 'X/Twitter' : 'Google'} account has been removed`,
      });
      
      // Invalidate queries to refresh data
      queryClient.invalidateQueries({ queryKey: ['/api/v1/auth/linked-accounts'] });
    },
    onError: (error: any, provider) => {
      trackEvent('account_action', EventAction.ACCOUNT_UNLINKED, {
        provider,
        success: false,
        error: error.message,
      });

      const errorMessage = error.message || 'Failed to unlink account';
      
      toast({
        title: 'Failed to unlink',
        description: errorMessage,
        variant: 'destructive',
      });
    },
  });

  const handleLink = async (provider: 'x' | 'google') => {
    try {
      trackEvent('ui_interaction', EventAction.BUTTON_CLICK, {
        label: `link_${provider}_start`,
        component: 'account_management_modal',
      });

      // Set link mode in localStorage
      localStorage.setItem('oauth_link_mode', 'link');
      localStorage.setItem('oauth_link_provider', provider);

      setIsLinking(provider);

      // Close modal before redirect
      setAccountManagementModal({ show: false });

      // Initiate OAuth flow
      const redirectUri = `${import.meta.env.VITE_X_REDIRECT_URI}/oauth/callback`;
      
      if (provider === 'x') {
        await initiateOAuthFlow(redirectUri);
      } else {
        await initiateGoogleOAuthFlow(redirectUri);
      }
    } catch (error: any) {
      setIsLinking(null);
      
      toast({
        title: 'Failed to start linking',
        description: error.message || 'An unexpected error occurred',
        variant: 'destructive',
      });
      
      // Reopen modal on error
      setAccountManagementModal({ show: true });
    }
  };

  const handleUnlink = async (provider: 'x' | 'google') => {
    // Check if this is the primary provider
    const accountInfo = provider === 'x' ? linkedAccounts?.x : linkedAccounts?.google;
    
    if (accountInfo?.isPrimary) {
      toast({
        title: 'Cannot unlink primary provider',
        description: 'You must keep at least one authentication method linked to your account',
        variant: 'destructive',
      });
      return;
    }

    // Show confirmation
    const providerName = provider === 'x' ? 'X/Twitter' : 'Google';
    const confirmed = window.confirm(
      `Are you sure you want to unlink your ${providerName} account?\n\nYou can always link it again later.`
    );

    if (!confirmed) return;

    trackEvent('ui_interaction', EventAction.BUTTON_CLICK, {
      label: `unlink_${provider}_confirm`,
      component: 'account_management_modal',
    });

    setUnlinkingProvider(provider);
    
    try {
      await unlinkMutation.mutateAsync(provider);
      await queryClient.invalidateQueries({ queryKey: authKeys.linkedAccounts });
    } finally {
      setUnlinkingProvider(null);
    }
  };

  const handleCloseModal = () => {
    // Reset linking state when closing
    setIsLinking(null);
    setAccountManagementModal({ show: false });
  };

  return (
    <Dialog open={showModal} onOpenChange={handleCloseModal}>
      <DialogContent className="max-w-md bg-secondary border-gray-800 text-white">
        <DialogHeader>
          <DialogTitle className="text-xl font-semibold">Account Management</DialogTitle>
          <DialogDescription className="text-gray-400">
            Link multiple sign-in methods to your account
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Loading State */}
          {loadingAccounts && (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
              <span className="ml-3 text-gray-400">Loading account status...</span>
            </div>
          )}

          {/* Error State */}
          {fetchError && !loadingAccounts && (
            <div className="p-4 bg-red-500/10 border border-red-500/30 rounded-lg flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm text-red-300 font-semibold">Failed to load account status</p>
                <p className="text-xs text-red-400 mt-1">
                  {(fetchError as any)?.message || 'Please try again later'}
                </p>
              </div>
            </div>
          )}

          {/* Account Cards */}
          {!loadingAccounts && !fetchError && linkedAccounts && (
            <>
              {/* X/Twitter Account Card */}
              <LinkedAccountCard
                provider="x"
                isLinked={!!linkedAccounts.x}
                accountInfo={linkedAccounts.x}
                isLinking={isLinking === 'x'}
                onLink={() => handleLink('x')}
                onUnlink={() => handleUnlink('x')}
                unlinking={unlinkingProvider === 'x'}
              />

              {/* Google Account Card */}
              <LinkedAccountCard
                provider="google"
                isLinked={!!linkedAccounts.google}
                accountInfo={linkedAccounts.google}
                isLinking={isLinking === 'google'}
                onLink={() => handleLink('google')}
                onUnlink={() => handleUnlink('google')}
                unlinking={unlinkingProvider === 'google'}
              />
            </>
          )}

          {/* Info Section */}
          {!loadingAccounts && !fetchError && (
            <div className="p-3 bg-blue-500/10 border border-blue-500/30 rounded-lg">
              <p className="text-sm text-blue-300 flex items-start gap-2">
                <span className="text-lg leading-none">💡</span>
                <span>
                  Linking accounts allows you to sign in with either method. 
                  Your primary provider is the one you used to create your account.
                </span>
              </p>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default AccountManagementModal;
